import React from 'react';
import styled from 'styled-components';
import { Table } from 'rbx';
import gql from 'graphql-tag';
import swal from 'sweetalert';

import { useMutation } from '@apollo/react-hooks';
import { useStoreState } from 'easy-peasy';
import { isNil } from 'lodash';
import { toast } from 'react-toastify';

import { Table as CustomTable, Box } from '../../../components/elements';

import List from './List';
import AddEditForm from './AddEditForm';
import client from '../../../utils/apolloClient';

const Container = styled.div`
  .table-container {
    min-height: 30rem;
  }
`;

const createApiActionMutation = gql`
  mutation createApiActionMutation($input: CreateCmsApiActionInput) {
    create_cms_api_action(input: $input) {
      api_action_id
      description
      name
      status
      error {
        code
        description
      }
    }
  }
`;

const updateApiActionMutation = gql`
  mutation updateApiActionMutation($input: UpdateCmsApiActionInput) {
    update_cms_api_action(input: $input) {
      api_action_id
      description
      name
      status
      error {
        code
        description
      }
    }
  }
`;

const deleteApiActionMutation = gql`
  mutation deleteApiActionMutation($input: DeleteCmsApiActionInput) {
    delete_cms_api_action(input: $input) {
      api_action_id
      description
      name
      status
      error {
        code
        description
      }
    }
  }
`;

const Details = ({ apiActions, activeModalProp, refetch }) => {
  const { userId } = useStoreState(state => state.auth);

  const {
    selectedApiAction,
    setSelectedApiAction,
    isActiveModal,
    setIsActiveModal,
  } = activeModalProp;

  const [createApiAction, { loading }] = useMutation(createApiActionMutation, {
    client: client.clientPrivate,
    onCompleted: ({ create_cms_api_action }) => {
      if (!isNil(create_cms_api_action.error)) {
        create_cms_api_action.error.map(item => toast.error(item.description));
      } else {
        swal('Successfully added api action!').then(() => {
          setIsActiveModal(false);
          refetch();
        });
      }
    },
  });

  const [updateApiAction, { loading: loading2 }] = useMutation(
    updateApiActionMutation,
    {
      client: client.clientPrivate,
      onCompleted: ({ update_cms_api_action }) => {
        if (!isNil(update_cms_api_action.error)) {
          update_cms_api_action.error.map(item =>
            toast.error(item.description),
          );
        } else {
          swal('Successfully updated api action!').then(() => {
            setIsActiveModal(false);
            refetch();
            setSelectedApiAction(null);
          });
        }
      },
    },
  );

  const [deleteApiAction, { loading: isDeleting }] = useMutation(
    deleteApiActionMutation,
    {
      client: client.clientPrivate,
      onCompleted: ({ delete_cms_api_action }) => {
        if (!isNil(delete_cms_api_action.error)) {
          delete_cms_api_action.error.map(item =>
            toast.error(item.description),
          );
        } else {
          swal('Successfully deleted api action!').then(() => {
            refetch();
            setSelectedApiAction(null);
          });
        }
      },
    },
  );

  return (
    <React.Fragment>
      <Box>
        <Container>
          <CustomTable>
            <Table.Head>
              <Table.Row>
                <Table.Heading>Name</Table.Heading>
                <Table.Heading>Description</Table.Heading>
                <Table.Heading>Actions</Table.Heading>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <List
                apiActions={apiActions}
                onEdit={value => {
                  setSelectedApiAction(value);
                  setIsActiveModal(true);
                }}
                onDelete={async value => {
                  deleteApiAction({
                    variables: {
                      input: {
                        api_action_id: value?.api_action_id,
                        user_id: userId,
                      },
                    },
                  });
                }}
                isDeleting={isDeleting}
              />
            </Table.Body>
          </CustomTable>
        </Container>
      </Box>
      {isActiveModal && (
        <AddEditForm
          apiAction={selectedApiAction}
          loading={loading || loading2}
          isActive={isActiveModal}
          onClose={() => setIsActiveModal(false)}
          onSubmit={values => {
            if (selectedApiAction) {
              updateApiAction({
                variables: {
                  input: {
                    api_action_id: selectedApiAction?.api_action_id,
                    user_id: userId,
                    name: values.name,
                    description: values.description,
                  },
                },
              });
            } else {
              createApiAction({
                variables: {
                  input: {
                    user_id: userId,
                    name: values.name,
                    description: values.description,
                  },
                },
              });
            }
          }}
        />
      )}
    </React.Fragment>
  );
};

export default Details;
